import { LOCATION_TO_PAGING } from "@helpers/location-to-paging";
import { getStoredPageSize, setStoredPageSize } from "@hooks/use-grid-pagesize";
import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from "react";
import { useLocation } from "react-router-dom";

export type PaginationModel = {
  page: number;
  pageSize: number;
};

type PaginationState = Record<string, PaginationModel>;

type TablePaginationContextType = {
  get: (tableKey: string) => PaginationModel;
  set: (tableKey: string, model: PaginationModel) => void;
  reset: (tableKey: string) => void;
  resetAll: () => void;
};

const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_PAGINATION: PaginationModel = {
  page: 0,
  pageSize: DEFAULT_PAGE_SIZE,
};

const TablePaginationContext = createContext<
  TablePaginationContextType | undefined
>(undefined);

export const TablePaginationProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const location = useLocation();
  const [paginationState, setPaginationState] = useState<PaginationState>({});

  const get = (tableKey: string): PaginationModel => {
    return (
      paginationState[tableKey] ?? {
        page: 0,
        pageSize: getStoredPageSize(tableKey, DEFAULT_PAGINATION.pageSize),
      }
    );
  };

  const set = (tableKey: string, model: PaginationModel) => {
    setPaginationState((prev) => ({
      ...prev,
      [tableKey]: model,
    }));

    setStoredPageSize(tableKey, model.pageSize);
  };

  const reset = (tableKey: string) => {
    setPaginationState((prev) => {
      const newState = { ...prev };
      delete newState[tableKey];
      return newState;
    });
  };

  const resetAll = () => {
    setPaginationState({});
  };

  useEffect(() => {
    Object.entries(LOCATION_TO_PAGING).forEach(([tableKey, cfg]) => {
      if (!cfg.valid.test(location.pathname)) {
        reset(tableKey);
      }
    });
  }, [location.pathname]);

  return (
    <TablePaginationContext.Provider value={{ get, set, reset, resetAll }}>
      {children}
    </TablePaginationContext.Provider>
  );
};

export const useTablePagination = (tableKey?: string) => {
  const ctx = useContext(TablePaginationContext);
  if (!ctx) {
    throw new Error(
      "useTablePagination must be used within TablePaginationProvider",
    );
  }

  const { get, set, reset, resetAll } = ctx;

  if (!tableKey) {
    return { resetAll };
  }

  const model = get(tableKey);

  const setModel = (newModel: PaginationModel) => {
    if (newModel.pageSize !== model.pageSize) {
      set(tableKey, { page: 0, pageSize: newModel.pageSize });
    } else {
      set(tableKey, newModel);
    }
  };

  return {
    page: model.page,
    pageSize: model.pageSize,
    setModel,
    reset,
    resetAll,
  };
};
